import React, { ReactElement, useEffect, useState } from 'react';
import {
  ProjectDatesFieldsConfiguration,
  ProjectDetailsField, ProjectDetailsFieldType,
  ProjectType
} from 'nekst-api';
import styles from './SetProjectDatesFieldsForm.module.scss';
import {
  Dropdown,
  FormTemplate,
  Header4, Row6x6x,
  SimpleBlock,
  useFormContextRequired,
  useListContextRequired, usePopupManager
} from 'ui-builder';
import { get } from 'lodash';
import { useTeamSetupAreaContext } from '../../markets/TeamSetupAreaContext';
import ConfirmWarningActionPopup from '../../../shared/web/popup/ConfirmWarningActionPopup';

function ChooseDateDropdown(
  props: {
    source: string,
    label: string,
    allFields: ProjectDetailsField[],
    exceptFieldSource: string,
  }
) {

  const formContext = useFormContextRequired<ProjectDatesFieldsConfiguration>();

  const data = formContext.data;

  const exceptFieldId = get(data, props.exceptFieldSource);

  return (
    <Dropdown
      source={props.source}
      label={props.label}
      options={props.allFields.filter((item) => item.id !== exceptFieldId)
        .map((item) => ({
          value: item.id,
          label: item.name
        }))}
    />
  );
}

function ProjectDataUpdater(
  props: {
    projectType: ProjectType,
  }
) {
  const formContext = useFormContextRequired<ProjectDatesFieldsConfiguration>();

  const listContext = useListContextRequired<ProjectDetailsField>();

  useEffect(() => {
    formContext.setFormData({
      startDateFieldId: listContext.data.find((item) => item.isProjectStartDate)?.id,
      endDateFieldId: listContext.data.find((item) => item.isProjectEndDate)?.id
    });
  }, [props.projectType, listContext.version]);

  return null;
}

function SafeguardForStartAndEndDates(props: {
  children: ReactElement,
  dateFields?: ProjectDetailsField[],
  dependencies?: any[],
}) {

  const popupManager = usePopupManager();

  const [contentDisabled, setContentDisabled] = useState(true);

  useEffect(() => {
    setContentDisabled(true);
  }, props.dependencies || []);

  const startDate = props.dateFields?.find((item) => item.isProjectStartDate);
  const endDate = props.dateFields?.find((item) => item.isProjectEndDate);

  return (
    <SimpleBlock
      isContentDisabled={contentDisabled && (!!startDate || !!endDate)}
      onClick={async () => {
        if (contentDisabled && (!!startDate || !!endDate)) {
          popupManager.openPopup((
            <ConfirmWarningActionPopup
              title="Change Start and End Date Fields"
              message={`Changing start and end date might affect workflow task links to detail boxes. Click "I understand" to proceed.`}
              submitFormFunc={async () => {
                setContentDisabled(false);
              }}
              buttonText="I understand"
            />
          ));
        }
      }}
    >
      {props.children}
    </SimpleBlock>
  );
}

export function BaseSetProjectDatesFieldsForm(
  props: {
    projectType: ProjectType,
    submitFormFunc: (data: ProjectDatesFieldsConfiguration) => Promise<void>,
  }
) {

  const listContext = useListContextRequired<ProjectDetailsField>();

  const [dateFields, setDateFields] = useState<ProjectDetailsField[]>();

  useEffect(() => {
    if (listContext.data?.length && !listContext.isLoading) {
      setDateFields(listContext
        .data
        .filter((item) => !item.isHidden)
        .filter((item) => item.fieldType === ProjectDetailsFieldType.DATE)
      );
    }
  }, [listContext.version, listContext.data?.length, props.projectType]);

  const setupAreaContext = useTeamSetupAreaContext();

  if (dateFields && (!setupAreaContext || !setupAreaContext.teamSetupArea.isCustom)) {
    return (
      <SafeguardForStartAndEndDates
        dependencies={[props.projectType]}
        dateFields={dateFields}
      >
        <SimpleBlock className={styles.block}>
          <Header4>For Creating Transactions Using Contracts:</Header4>
          <FormTemplate<ProjectDatesFieldsConfiguration>
            submitFormFunc={async (data) => {
              await props.submitFormFunc(data);
              return data;
            }}
            buttons={null}
            dependencies={[props.projectType]}
            submitOnEachChange
          >
            <Row6x6x>
              <ChooseDateDropdown
                source="startDateFieldId"
                label="Start Date"
                allFields={dateFields}
                exceptFieldSource="endDateFieldId"
              />
              <ChooseDateDropdown
                source="endDateFieldId"
                label="End Date"
                allFields={dateFields}
                exceptFieldSource="startDateFieldId"
              />
              <ProjectDataUpdater projectType={props.projectType} />
            </Row6x6x>
          </FormTemplate>
        </SimpleBlock>
      </SafeguardForStartAndEndDates>
    );
  } else {
    return null;
  }
}

